import { createSelector } from 'reselect'
import { AccountCurrencyOrder } from '~/constants/assets'
import { isFiatCurrency } from '~/helpers/currencyHelpers'
import { ConfigManager } from '~/managers/ConfigManager'
import { CommonState } from '~/store/types'
import {
  ACCOUNT_TYPES,
  BANK_TRANSFER_NETWORK,
  CURRENCY,
  CURRENCY_TYPES,
  DGWAccountLimit,
  WALLET_TYPES,
  WalletIdType,
} from '~/types'
import { Account, STATE_STATUS } from './types'

const parseAccountLimits = (
  accountDetail: Account,
  network: BANK_TRANSFER_NETWORK
): DGWAccountLimit => {
  const limits = accountDetail?.limitations

  return {
    withdrawalDailyRemaining: limits?.[network]?.withdrawalDaily?.remaining || '0',
    withdrawalDailyMax: limits?.[network]?.withdrawalDaily?.max || '0',
  } as DGWAccountLimit
}

const selectAssetsState = (state: CommonState) => state.assets

export const selectAccounts = createSelector([selectAssetsState], (state) => state.accountList)

export const selectDgwAccounts = createSelector([selectAccounts], (accounts) =>
  accounts.filter((acc) => acc.walletId === WALLET_TYPES.DIGITAL_GOODS)
)

export const selectAccountByWalletIdTypeAndCurency = createSelector(
  [selectAccounts],
  (accounts) => (walletIdType: WalletIdType | null, currency: CURRENCY | null) => {
    if (!walletIdType || !currency) {
      return undefined
    }

    return accounts.find((acc) => {
      return acc.walletId === walletIdType && acc.currency === currency
    })
  }
)

export const selectDgwAccountsByCurencyType = createSelector(
  [selectDgwAccounts],
  (accounts) => (currencyType: CURRENCY_TYPES) => {
    const filtered = accounts.filter((acc) => {
      if (currencyType === CURRENCY_TYPES.FIAT) {
        return isFiatCurrency(acc.currency)
      }

      return !isFiatCurrency(acc.currency)
    })

    return filtered.sort(
      (a, b) => AccountCurrencyOrder[a.currency] - AccountCurrencyOrder[b.currency]
    )
  }
)

export const selectDgwAccountByCurrency = createSelector(
  [selectDgwAccounts],
  (accounts) => (currency: CURRENCY | null) => accounts.find((acc) => acc.currency === currency)
)

export const selectDgwAccountLimitByCurrency = createSelector(
  [selectDgwAccounts],
  (accounts) => (currency: CURRENCY | null, network: BANK_TRANSFER_NETWORK | null) => {
    if (!currency || !network) {
      return undefined
    }

    const accountType = ConfigManager.isAppSite() ? ACCOUNT_TYPES.PERSONAL : ACCOUNT_TYPES.BUSINESS
    const digitalGoodsWallet = accounts.find(
      (acc) => acc.currency === currency && acc.type === accountType
    )

    if (!digitalGoodsWallet) {
      return undefined
    }

    return parseAccountLimits(digitalGoodsWallet, network)
  }
)

export const selectIsLoading = createSelector(
  [selectAssetsState],
  (state) => state.status === STATE_STATUS.LOADING
)

export const selectTotalBalance = createSelector([selectAssetsState], (state) => state.totalBalance)

export const selectIsAccountListInitiated = createSelector(
  [selectAssetsState],
  (state) => state.isAccountListInitiated
)
